import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, CircleMarker, Tooltip, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import useTourStore from "../store/tourStore";
import POIDiscovery from "../components/POIDiscovery";
import SavedPOIs from "../components/SavedPOIs";
import LocationButton from "../components/LocationButton";
import { api } from "../lib/api";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom POI icons
const createPOIIcon = (tier) => {
  const colors = {
    'A': '#ff4444',
    'B': '#ff9944',
    'C': '#44aaff',
  };
  const color = colors[tier] || '#666';
  
  return L.divIcon({
    className: 'custom-poi-marker',
    html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

function TourEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTour, loading, error, fetchTour, updateTour } = useTourStore();
  
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [waypoints, setWaypoints] = useState([]);
  const [transportType, setTransportType] = useState('walk'); // walk, car, bike
  const [saving, setSaving] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [discoveredPOIs, setDiscoveredPOIs] = useState([]);
  const [savedPOIs, setSavedPOIs] = useState([]);
  const [highlightedPOI, setHighlightedPOI] = useState(null);
  const [highlightedWaypoint, setHighlightedWaypoint] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'saved', 'pois', or 'waypoints'
  const [computingRoute, setComputingRoute] = useState(false);
  const [routePolyline, setRoutePolyline] = useState([]);
  const mapRef = useRef(null);
  const [showWaypoints, setShowWaypoints] = useState(false);
  const [showSavedPOIs, setShowSavedPOIs] = useState(true);
  const [showPOIRadius, setShowPOIRadius] = useState(false);
  const [showPOINames, setShowPOINames] = useState(true);
  const [showRoute, setShowRoute] = useState(true);
  const [movingPOI, setMovingPOI] = useState(null);
  const [movingWaypoint, setMovingWaypoint] = useState(null); // { index, lat, lon }
  const [draggedWaypoint, setDraggedWaypoint] = useState(null); // index of waypoint being dragged
  const [dragOverIndex, setDragOverIndex] = useState(null); // index being dragged over
  const [publishing, setPublishing] = useState(false);
  const [editingRadiusOnMap, setEditingRadiusOnMap] = useState(null); // { poiId, radius }
  const [routeDistance, setRouteDistance] = useState(null); // distance in meters
  const [routeDuration, setRouteDuration] = useState(null); // duration in seconds
  const [customModeActive, setCustomModeActive] = useState(false);
  const [customPointClickHandler, setCustomPointClickHandler] = useState(null);
  const [previewMarker, setPreviewMarker] = useState(null); // { lat, lon } for custom point preview

  useEffect(() => {
    if (id) {
      fetchTour(id);
    }
  }, [id, fetchTour]);

  useEffect(() => {
    if (currentTour) {
      setFormData({
        title: currentTour.title,
        description: currentTour.description || '',
      });
    }
  }, [currentTour]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTour(id, formData);
      setEditMode(false);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!confirm('Are you sure you want to publish this tour? It will be accessible via a public share link.')) {
      return;
    }
    
    setPublishing(true);
    try {
      const updatedTour = await api.publishTour(id);
      await fetchTour(id); // Refresh the tour data
      alert(`Tour published! Share ID: ${updatedTour.share_id}`);
    } catch (err) {
      alert('Failed to publish tour: ' + err.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!confirm('Are you sure you want to unpublish this tour? The public share link will no longer work.')) {
      return;
    }
    
    setPublishing(true);
    try {
      await api.unpublishTour(id);
      await fetchTour(id); // Refresh the tour data
      alert('Tour unpublished successfully');
    } catch (err) {
      alert('Failed to unpublish tour: ' + err.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleRefreshShareID = async () => {
    if (!confirm('Refresh tour metadata (version and last updated)?')) {
      return;
    }
    
    setPublishing(true);
    try {
      const updatedTour = await api.refreshShareID(id);
      await fetchTour(id); // Refresh the tour data
      alert(`Tour metadata refreshed. Current version: ${updatedTour.current_version}`);
    } catch (err) {
      alert('Failed to refresh share ID: ' + err.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleStartMovePOI = (poi) => {
    setMovingPOI(poi);
    console.log('Started moving POI:', poi.id, poi.name);
  };

  const handleMovePOI = async (latlng) => {
    console.log('handleMovePOI called, movingPOI:', movingPOI);
    if (!movingPOI) return;

    try {
      const updated = await api.updateSavedPOI(id, movingPOI.id, {
        lat: latlng.lat,
        lon: latlng.lng,
      });
      const updatedPOIs = savedPOIs.map(poi => poi.id === movingPOI.id ? updated : poi);
      setSavedPOIs(updatedPOIs);
      setMovingPOI(null);
      console.log('POI moved successfully to:', latlng.lat, latlng.lng);
      
      // Force map to re-render by reloading saved POIs
      await loadSavedPOIs();
    } catch (error) {
      alert('Failed to move POI: ' + error.message);
      setMovingPOI(null);
    }
  };

  const handleStartEditRadiusOnMap = (poi) => {
    setEditingRadiusOnMap({ poiId: poi.id, radius: poi.radius_m || 50 });
  };

  const handleSaveRadiusOnMap = async (poiId) => {
    if (!editingRadiusOnMap) return;
    
    const radius = editingRadiusOnMap.radius;
    if (radius < 10 || radius > 500) {
      alert('Radius must be between 10 and 500 meters');
      return;
    }

    try {
      const updated = await api.updateSavedPOI(id, poiId, {
        radius_m: radius,
      });
      const updatedPOIs = savedPOIs.map(poi => poi.id === poiId ? updated : poi);
      setSavedPOIs(updatedPOIs);
      setEditingRadiusOnMap(null);
      
      // Force map to re-render by reloading saved POIs
      await loadSavedPOIs();
    } catch (error) {
      alert('Failed to update radius: ' + error.message);
    }
  };

  const handleCancelEditRadiusOnMap = () => {
    setEditingRadiusOnMap(null);
  };

  const handleAddWaypoint = async (latlng) => {
    console.log('handleAddWaypoint called, movingPOI:', movingPOI, 'movingWaypoint:', movingWaypoint);
    if (movingPOI) {
      handleMovePOI(latlng);
    } else if (movingWaypoint) {
      handleMoveWaypointPosition(latlng);
    } else if (activeTab === 'waypoints') {
      // Only allow adding waypoints when on the waypoints tab
      const updatedWaypoints = [...waypoints, { lat: latlng.lat, lon: latlng.lng }];
      setWaypoints(updatedWaypoints);
      
      // Auto-compute route if we have at least 2 waypoints
      if (updatedWaypoints.length >= 2) {
        setComputingRoute(true);
        try {
          const response = await api.computeRoute(id, updatedWaypoints, transportType);
          setRoutePolyline(response.route || []);
          
          // Update route metadata
          if (response.distance_m !== undefined) {
            setRouteDistance(response.distance_m);
          }
          if (response.duration_sec !== undefined) {
            setRouteDuration(response.duration_sec);
          }
        } catch (err) {
          console.error('Failed to compute route:', err);
        } finally {
          setComputingRoute(false);
        }
      }
    }
  };

  const handleRemoveWaypoint = async (index) => {
    const updatedWaypoints = waypoints.filter((_, i) => i !== index);
    setWaypoints(updatedWaypoints);
    
    // Persist to server if there are remaining waypoints
    if (updatedWaypoints.length >= 2) {
      try {
        await api.computeRoute(id, updatedWaypoints, transportType);
        await loadRoute(); // Reload to get the updated route
      } catch (err) {
        console.error('Failed to update route after waypoint removal:', err);
      }
    } else if (updatedWaypoints.length === 0) {
      // If all waypoints removed, clear the route polyline
      setRoutePolyline([]);
    }
  };

  const handleMoveWaypoint = async (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === waypoints.length - 1)) {
      return; // Can't move first item up or last item down
    }

    const updatedWaypoints = [...waypoints];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap waypoints
    [updatedWaypoints[index], updatedWaypoints[newIndex]] = [updatedWaypoints[newIndex], updatedWaypoints[index]];
    
    setWaypoints(updatedWaypoints);

    // Update route if we have enough waypoints
    if (updatedWaypoints.length >= 2) {
      try {
        await api.computeRoute(id, updatedWaypoints, transportType);
        await loadRoute(); // Reload to get the updated route
      } catch (err) {
        console.error('Failed to update route after reordering:', err);
      }
    }
  };

  const handleStartMoveWaypoint = (index) => {
    setMovingWaypoint({ index, ...waypoints[index] });
    console.log('Started moving waypoint:', index);
  };

  const handleMoveWaypointPosition = async (latlng) => {
    console.log('handleMoveWaypointPosition called, movingWaypoint:', movingWaypoint);
    if (!movingWaypoint) return;

    try {
      const updatedWaypoints = [...waypoints];
      updatedWaypoints[movingWaypoint.index] = { lat: latlng.lat, lon: latlng.lng };
      setWaypoints(updatedWaypoints);
      setMovingWaypoint(null);
      console.log('Waypoint moved successfully to:', latlng.lat, latlng.lng);

      // Auto-compute route if we have at least 2 waypoints
      if (updatedWaypoints.length >= 2) {
        setComputingRoute(true);
        try {
          const response = await api.computeRoute(id, updatedWaypoints, transportType);
          setRoutePolyline(response.route || []);
          
          if (response.distance_m !== undefined) {
            setRouteDistance(response.distance_m);
          }
          if (response.duration_sec !== undefined) {
            setRouteDuration(response.duration_sec);
          }
        } catch (err) {
          console.error('Failed to compute route:', err);
          alert('Failed to compute route: ' + err.message);
        } finally {
          setComputingRoute(false);
        }
      }
    } catch (error) {
      alert('Failed to move waypoint: ' + error.message);
      setMovingWaypoint(null);
    }
  };

  const handlePOIsDiscovered = (pois) => {
    setDiscoveredPOIs(pois);
  };

  const handlePOIsSaved = async () => {
    // Reload saved POIs after saving
    await loadSavedPOIs();
    setActiveTab('saved'); // Switch to saved tab
  };

  const handlePOIClick = (poi) => {
    setHighlightedPOI(poi.id);
    // Pan and zoom map to POI location
    if (mapRef.current) {
      mapRef.current.setView([poi.lat, poi.lon], 16, {
        animate: true,
        duration: 0.5
      });
    }
  };

  const handleWaypointClick = (waypoint) => {
    // Pan and zoom map to waypoint location
    if (mapRef.current) {
      mapRef.current.setView([waypoint.lat, waypoint.lon], 16, {
        animate: true,
        duration: 0.5
      });
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedWaypoint(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedWaypoint !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedWaypoint === null || draggedWaypoint === dropIndex) {
      setDraggedWaypoint(null);
      return;
    }

    const updatedWaypoints = [...waypoints];
    const [draggedItem] = updatedWaypoints.splice(draggedWaypoint, 1);
    updatedWaypoints.splice(dropIndex, 0, draggedItem);
    
    setWaypoints(updatedWaypoints);
    setDraggedWaypoint(null);

    // Update route if we have enough waypoints
    if (updatedWaypoints.length >= 2) {
      try {
        await api.computeRoute(id, updatedWaypoints, transportType);
        await loadRoute();
      } catch (err) {
        console.error('Failed to update route after reordering:', err);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedWaypoint(null);
    setDragOverIndex(null);
  };

  const handleSaveDiscoveredPOI = async (poi) => {
    if (!id) {
      alert('Tour ID is required to save POI');
      return;
    }

    try {
      const request = {
        poi_id: poi.id,
        osm_type: poi.osm_type,
        osm_id: poi.osm_id,
        name: poi.name,
        description: poi.description || null,
        lat: poi.lat,
        lon: poi.lon,
        tags: poi.tags || {},
        wikidata_id: poi.wikidata_id || null,
        score: poi.score,
        tier: poi.tier || null,
      };
      await api.savePOI(id, request);
      await loadSavedPOIs();
      // Remove from discovered list
      setDiscoveredPOIs(discoveredPOIs.filter(p => p.id !== poi.id));
    } catch (error) {
      alert('Failed to save POI: ' + error.message);
    }
  };

  const handleComputeRoute = async () => {
    if (waypoints.length < 2) {
      alert('At least 2 waypoints are required to compute a route');
      return;
    }

    setComputingRoute(true);
    try {
      const response = await api.computeRoute(id, waypoints, transportType);
      setRoutePolyline(response.route || []);
      
      // Update route metadata
      if (response.distance_m !== undefined) {
        setRouteDistance(response.distance_m);
      }
      if (response.duration_sec !== undefined) {
        setRouteDuration(response.duration_sec);
      }
    } catch (err) {
      alert('Failed to compute route: ' + err.message);
    } finally {
      setComputingRoute(false);
    }
  };

  const handleRemoveRoute = async () => {
    if (!confirm('Are you sure you want to remove all waypoints and the route?')) {
      return;
    }

    setComputingRoute(true);
    try {
      // Send empty waypoints array to clear the route
      await api.computeRoute(id, [], transportType);
      setWaypoints([]);
      setRoutePolyline([]);
      setRouteDistance(null);
      setRouteDuration(null);
    } catch (err) {
      alert('Failed to remove route: ' + err.message);
    } finally {
      setComputingRoute(false);
    }
  };

  const loadRoute = async () => {
    try {
      const response = await api.getRoute(id);
      if (response.route && response.route.length > 0) {
        setRoutePolyline(response.route);
      }
      if (response.waypoints && response.waypoints.length > 0) {
        setWaypoints(response.waypoints);
      }
      // Store distance and duration
      if (response.distance_m !== undefined) {
        setRouteDistance(response.distance_m);
      }
      if (response.duration_sec !== undefined) {
        setRouteDuration(response.duration_sec);
      }
      // Set transport type based on stored profile
      if (response.profile) {
        setTransportType(response.profile);
      }
    } catch (err) {
      console.error('Failed to load route:', err);
    }
  };

  useEffect(() => {
    if (id) {
      loadRoute();
      loadSavedPOIs();
    }
  }, [id]);

  const loadSavedPOIs = async () => {
    if (!id) return;
    try {
      const response = await api.getSavedPOIs(id);
      setSavedPOIs(response.pois || []);
    } catch (error) {
      console.error('Failed to load saved POIs:', error);
    }
  };

  if (loading && !currentTour) {
    return <div className="loading">Loading tour...</div>;
  }

  if (error && !currentTour) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="button button-primary">
          Back to Tours
        </button>
      </div>
    );
  }

  if (!currentTour) {
    return null;
  }

  return (
    <div className="tour-editor">
      <div className="editor-header">
        <div>
          <button onClick={() => navigate('/')} className="button button-text">
            ← Back to Tours
          </button>
          <h2>{currentTour.title}</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {currentTour.status === 'draft' ? (
            <button 
              onClick={handlePublish} 
              className="button button-primary"
              disabled={publishing}
            >
              {publishing ? 'Publishing...' : '📤 Publish'}
            </button>
          ) : (
            <>
              <button 
                onClick={handleUnpublish} 
                className="button button-secondary"
                disabled={publishing}
              >
                {publishing ? 'Unpublishing...' : '🔒 Unpublish'}
              </button>
              <button 
                onClick={handleRefreshShareID} 
                className="button button-secondary"
                disabled={publishing}
                title="Refresh tour version and last updated timestamp"
              >
                {publishing ? 'Refreshing...' : '🔄 Refresh'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="editor-layout">
        {/* Left Panel - Tour Details & POI Discovery */}
        <div className="editor-sidebar">
          {/* Tab Navigation */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`tab ${activeTab === 'saved' ? 'active' : ''}`}
              onClick={() => setActiveTab('saved')}
            >
              Spots
            </button>
            <button
              className={`tab ${activeTab === 'pois' ? 'active' : ''}`}
              onClick={() => setActiveTab('pois')}
            >
              Discover
            </button>
            <button
              className={`tab ${activeTab === 'waypoints' ? 'active' : ''}`}
              onClick={() => setActiveTab('waypoints')}
            >
              Route
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <>
              {/* Map Visualization Controls - Only on Tour Details Tab */}
              <div className="card" style={{ padding: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Map Layers</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showRoute}
                      onChange={(e) => setShowRoute(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Route</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showWaypoints}
                      onChange={(e) => setShowWaypoints(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Route Waypoints</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showSavedPOIs}
                      onChange={(e) => setShowSavedPOIs(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Spots</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showPOIRadius}
                      onChange={(e) => setShowPOIRadius(e.target.checked)}
                      disabled={!showSavedPOIs}
                      style={{ cursor: showSavedPOIs ? 'pointer' : 'not-allowed', opacity: showSavedPOIs ? 1 : 0.5 }}
                    />
                    <span>Spot Radius</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showPOINames}
                      onChange={(e) => setShowPOINames(e.target.checked)}
                      disabled={!showSavedPOIs}
                      style={{ cursor: showSavedPOIs ? 'pointer' : 'not-allowed', opacity: showSavedPOIs ? 1 : 0.5 }}
                    />
                    <span>Spot Names</span>
                  </label>
                </div>
              </div>
            </>
          ) : null}

          {activeTab === 'details' ? (
            <>
              <div className="card">
                <div className="card-header">
                  <h3>Tour Details</h3>
                  {!editMode ? (
                    <button 
                      onClick={() => setEditMode(true)} 
                      className="button button-sm button-secondary"
                    >
                      Edit
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => {
                          setEditMode(false);
                          setFormData({
                            title: currentTour.title,
                            description: currentTour.description || '',
                          });
                        }} 
                        className="button button-sm button-secondary"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSave} 
                        className="button button-sm button-primary"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {editMode ? (
                  <div>
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="4"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p><strong>Base ID:</strong> {currentTour.base_id}</p>
                    <p><strong>Language:</strong> {currentTour.lang}</p>
                    <p>
                      <strong>Status:</strong>{' '}
                      <span className={`badge badge-${currentTour.status}`}>
                        {currentTour.status}
                      </span>
                    </p>
                    <p><strong>Description:</strong></p>
                    <p>{currentTour.description || 'No description'}</p>
                  </div>
                )}
              </div>
            </>
          ) : activeTab === 'saved' ? (
            <SavedPOIs
              tourId={id}
              tourLang={currentTour?.lang}
              onPOIClick={handlePOIClick}
              highlightedPOI={highlightedPOI}
              onPOIsChanged={setSavedPOIs}
            />
          ) : activeTab === 'pois' ? (
            <POIDiscovery
              tourId={id}
              mapBounds={mapBounds}
              onPOIsDiscovered={handlePOIsDiscovered}
              onPOIsSaved={handlePOIsSaved}
              onCustomModeChange={setCustomModeActive}
              onMapClick={setCustomPointClickHandler}
              onPreviewMarkerChange={setPreviewMarker}
            />
          ) : activeTab === 'waypoints' ? (
            <>
              <div className="card">
                <h3>Waypoints ({waypoints.length})</h3>
                <p className="help-text">
                  Click on the map to add waypoints for your tour route.
                </p>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Transport Type:
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={`button ${transportType === 'walk' ? 'button-primary' : ''}`}
                      onClick={() => setTransportType('walk')}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      Walk
                    </button>
                    <button
                      className={`button ${transportType === 'bike' ? 'button-primary' : ''}`}
                      onClick={() => setTransportType('bike')}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      Bike
                    </button>
                    <button
                      className={`button ${transportType === 'car' ? 'button-primary' : ''}`}
                      onClick={() => setTransportType('car')}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      Car
                    </button>
                  </div>
                </div>

                {/* Route Information */}
                {(routeDistance !== null || routeDuration !== null) && (
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '0.75rem', 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: '4px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#1e40af' }}>
                      Route Information
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.9rem' }}>
                      {routeDistance !== null && (
                        <div>
                          <strong>Distance:</strong> {(routeDistance / 1000).toFixed(2)} km
                        </div>
                      )}
                      {routeDuration !== null && (
                        <div>
                          <strong>Duration:</strong> {
                            routeDuration >= 3600 
                              ? `${Math.floor(routeDuration / 3600)}h ${Math.floor((routeDuration % 3600) / 60)}m`
                              : `${Math.floor(routeDuration / 60)}m`
                          }
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {waypoints.length === 0 ? (
                  <p className="empty-state">No waypoints yet. Click the map to add one.</p>
                ) : (
                  <div className="waypoints-list">
                    {waypoints.map((wp, index) => (
                      <div 
                        id={`waypoint-${index}`}
                        key={index} 
                        className="waypoint-item"
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          cursor: 'grab',
                          opacity: draggedWaypoint === index ? 0.5 : 1,
                          transition: 'all 0.2s',
                          backgroundColor: highlightedWaypoint === index ? '#e0e7ff' : 
                                         dragOverIndex === index ? '#dbeafe' : undefined,
                          borderColor: highlightedWaypoint === index ? '#667eea' : 
                                      dragOverIndex === index ? '#3b82f6' : undefined,
                          borderWidth: dragOverIndex === index ? '2px' : undefined,
                        }}
                        onClick={() => {
                          handleWaypointClick(wp);
                          setHighlightedWaypoint(index);
                        }}
                      >
                        <span style={{ marginRight: '0.5rem', color: '#9ca3af', fontSize: '1.2em', cursor: 'grab' }}>⋮⋮</span>
                        <span style={{ flex: 1 }}>
                          {index + 1}. {wp.lat.toFixed(5)}, {wp.lon.toFixed(5)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveWaypoint(index);
                          }}
                          className="button button-sm button-danger"
                          style={{ padding: '0.25rem 0.5rem' }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  {waypoints.length > 1 && (
                    <button 
                      className="button button-primary" 
                      style={{ flex: 1 }}
                      onClick={handleComputeRoute}
                      disabled={computingRoute}
                    >
                      {computingRoute ? 'Computing...' : 'Compute Route'}
                    </button>
                  )}
                  {waypoints.length > 0 && (
                    <button 
                      className="button button-danger" 
                      style={{ flex: waypoints.length > 1 ? 0 : 1, minWidth: waypoints.length > 1 ? '120px' : 'auto' }}
                      onClick={handleRemoveRoute}
                      disabled={computingRoute}
                    >
                      Remove Route
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : activeTab === 'details' ? (
            <>
              <div className="card">
                <div className="card-header">
                  <h3>Tour Details</h3>
                  {!editMode ? (
                    <button 
                      onClick={() => setEditMode(true)} 
                      className="button button-sm button-secondary"
                    >
                      Edit
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => {
                          setEditMode(false);
                          setFormData({
                            title: currentTour.title,
                            description: currentTour.description || '',
                          });
                        }} 
                        className="button button-sm button-secondary"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSave} 
                        className="button button-sm button-primary"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {editMode ? (
                  <div>
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="4"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p><strong>Base ID:</strong> {currentTour.base_id}</p>
                    <p><strong>Language:</strong> {currentTour.lang}</p>
                    <p><strong>Description:</strong></p>
                    <p>{currentTour.description || 'No description'}</p>
                  </div>
                )}
              </div>
            </>
          ) : activeTab === 'saved' ? (
            <SavedPOIs
              tourId={id}
              tourLang={currentTour?.lang}
              onPOIClick={handlePOIClick}
              highlightedPOI={highlightedPOI}
              onPOIsChanged={setSavedPOIs}
            />
          ) : activeTab === 'pois' ? (
            <POIDiscovery
              tourId={id}
              mapBounds={mapBounds}
              onPOIsDiscovered={handlePOIsDiscovered}
              onPOIsSaved={handlePOIsSaved}
              onCustomModeChange={setCustomModeActive}
              onMapClick={setCustomPointClickHandler}
              onPreviewMarkerChange={setPreviewMarker}
            />
          ) : null}
        </div>

        {/* Right Panel - Map */}
        <div className="editor-map">
          <MapContainer
            center={[51.505, -0.09]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapRefHandler mapRef={mapRef} />
            <MapBoundsHandler onBoundsChange={setMapBounds} />
            <MapClickHandler 
              onMapClick={customModeActive && customPointClickHandler ? customPointClickHandler : (movingPOI || movingWaypoint || activeTab === 'waypoints' ? handleAddWaypoint : null)} 
            />
            <FitBoundsToWaypoints waypoints={waypoints} savedPOIs={savedPOIs} />
            <LocationButton />
            
            {/* Route polyline */}
            {showRoute && routePolyline.length > 0 && (
              <Polyline
                positions={routePolyline.map(p => [p.lat, p.lon])}
                color="#667eea"
                weight={4}
                opacity={0.7}
              />
            )}
            
            {showWaypoints && waypoints.map((wp, index) => (
              <Marker 
                key={index} 
                position={[wp.lat, wp.lon]}
                icon={L.divIcon({
                  className: 'custom-waypoint-marker',
                  html: `<div style="
                    position: relative;
                    width: 28px;
                    height: 28px;
                    background-color: #667eea;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 12px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                  ">${index + 1}</div>`,
                  iconSize: [28, 28],
                  iconAnchor: [14, 14],
                })}
                eventHandlers={{
                  click: () => {
                    setActiveTab('waypoints');
                    setHighlightedWaypoint(index);
                    setTimeout(() => {
                      const element = document.getElementById(`waypoint-${index}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }
                }}
              >
                <Popup>
                  <strong>Waypoint {index + 1}</strong>
                  <br />
                  <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '0.3em' }}>
                    {wp.lat.toFixed(5)}, {wp.lon.toFixed(5)}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.5em' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartMoveWaypoint(index);
                      }}
                      style={{
                        padding: '0.4em 0.8em',
                        backgroundColor: movingWaypoint?.index === index ? '#ef4444' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8em',
                        fontWeight: '500',
                        flex: 1,
                      }}
                    >
                      {movingWaypoint?.index === index ? 'Click to place' : 'Move'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveWaypoint(index);
                      }}
                      style={{
                        padding: '0.4em 0.8em',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8em',
                        fontWeight: '500',
                        flex: 1,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
            {discoveredPOIs.map((poi) => (
              <Marker
                key={poi.id}
                position={[poi.lat, poi.lon]}
                icon={createPOIIcon(poi.tier)}
              >
                <Popup>
                  <strong>{poi.name || 'Unnamed POI'}</strong>
                  <br />
                  {poi.description && (
                    <>
                      <p style={{ fontSize: '0.9em', margin: '0.5em 0' }}>{poi.description}</p>
                    </>
                  )}
                  <div style={{ fontSize: '0.75em', color: '#999', marginBottom: '0.5em' }}>
                    Score: {poi.score}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.5em' }}>
                    <button 
                      onClick={() => handleSaveDiscoveredPOI(poi)}
                      style={{
                        padding: '0.4em 0.8em',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8em',
                        fontWeight: '500',
                        flex: 1,
                      }}
                    >
                      Save
                    </button>
                    {poi.wikidata_id && (
                      <a
                        href={`https://www.wikidata.org/wiki/${poi.wikidata_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: '0.8em',
                          color: '#667eea',
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.4em 0.8em',
                        }}
                      >
                        Wiki ↗
                      </a>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            
            {/* Preview marker for custom point */}
            {previewMarker && (
              <Marker
                position={[previewMarker.lat, previewMarker.lon]}
                icon={L.divIcon({
                  className: 'custom-preview-marker',
                  html: `<div style="background-color: #ff4444; width: 20px; height: 20px; border-radius: 50%; border: 4px solid white; box-shadow: 0 0 10px rgba(255,68,68,0.6); animation: pulse 1.5s ease-in-out infinite;"></div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
              >
                <Popup>
                  <div style={{ fontWeight: '600', color: '#ff4444' }}>
                    Preview Location
                  </div>
                  <div style={{ fontSize: '0.85em', marginTop: '0.3em', color: '#666' }}>
                    Enter a name to save this point
                  </div>
                </Popup>
              </Marker>
            )}
            
            {showSavedPOIs && savedPOIs.map((poi) => (
              <Marker
                key={`saved-${poi.id}`}
                position={[poi.lat, poi.lon]}
                icon={L.divIcon({
                  className: 'custom-saved-poi-marker',
                  html: `<div style="background-color: #10b981; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 6px rgba(0,0,0,0.4);"></div>`,
                  iconSize: [16, 16],
                  iconAnchor: [8, 8],
                })}
                eventHandlers={{
                  click: () => {
                    handlePOIClick(poi);
                    setActiveTab('saved');
                  }
                }}
              >
                {showPOINames && (
                  <Tooltip 
                    direction="top" 
                    offset={[0, -8]} 
                    opacity={1} 
                    permanent
                    className="poi-name-label"
                  >
                    <div style={{ 
                      fontSize: '0.75em', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      whiteSpace: 'nowrap',
                      border: '1px solid #10b981',
                    }}>
                      {poi.name}
                    </div>
                  </Tooltip>
                )}
                <Popup>
                  <strong>{poi.name}</strong>
                  <br />
                  {poi.description && (
                    <>
                      <p style={{ fontSize: '0.9em', margin: '0.5em 0', color: '#6b7280' }}>{poi.description}</p>
                    </>
                  )}
                  {poi.notes && (
                    <div style={{ fontSize: '0.85em', fontStyle: 'italic', color: '#6b7280', marginTop: '0.5em', padding: '0.5em', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                      {poi.notes}
                    </div>
                  )}
                  {poi.wikidata_id && (
                    <a
                      href={`https://www.wikidata.org/wiki/${poi.wikidata_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.8em', display: 'block', marginTop: '0.5em', color: '#667eea' }}
                    >
                      Wikidata ↗
                    </a>
                  )}
                  {editingRadiusOnMap?.poiId === poi.id ? (
                    <div style={{ marginTop: '0.5em' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.5em' }}>
                        <label style={{ fontSize: '0.8em', fontWeight: '500' }}>Radius:</label>
                        <input
                          type="number"
                          value={editingRadiusOnMap.radius}
                          onChange={(e) => setEditingRadiusOnMap({ ...editingRadiusOnMap, radius: parseInt(e.target.value) || 50 })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSaveRadiusOnMap(poi.id);
                            }
                          }}
                          min="10"
                          max="500"
                          style={{
                            width: '70px',
                            padding: '0.3em 0.5em',
                            fontSize: '0.8em',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                          }}
                        />
                        <span style={{ fontSize: '0.8em' }}>m</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5em' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveRadiusOnMap(poi.id);
                          }}
                          style={{
                            padding: '0.4em 0.8em',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            flex: 1,
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelEditRadiusOnMap();
                          }}
                          style={{
                            padding: '0.4em 0.8em',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            flex: 1,
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.5em' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartMovePOI(poi);
                          }}
                          style={{
                            padding: '0.4em 0.8em',
                            backgroundColor: movingPOI?.id === poi.id ? '#ef4444' : '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            flex: 1,
                          }}
                        >
                          {movingPOI?.id === poi.id ? 'Click to place' : 'Move'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEditRadiusOnMap(poi);
                          }}
                          style={{
                            padding: '0.4em 0.8em',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            flex: 1,
                          }}
                        >
                          Radius ({poi.radius_m}m)
                        </button>
                      </div>
                    </>
                  )}
                </Popup>
              </Marker>
            ))}
            
            {/* POI Radius Circles */}
            {showSavedPOIs && showPOIRadius && savedPOIs.map((poi) => (
              <Circle
                key={`radius-${poi.id}`}
                center={[poi.lat, poi.lon]}
                radius={poi.radius_m || 50}
                pathOptions={{
                  color: '#10b981',
                  fillColor: '#10b981',
                  fillOpacity: 0.1,
                  weight: 2,
                }}
              />
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
}

function MapRefHandler({ mapRef }) {
  const map = useMap();
  
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  
  return null;
}

function MapBoundsHandler({ onBoundsChange }) {
  const map = useMap();
  
  useEffect(() => {
    const updateBounds = () => {
      onBoundsChange(map.getBounds());
    };
    
    updateBounds();
    map.on('moveend', updateBounds);
    
    return () => {
      map.off('moveend', updateBounds);
    };
  }, [map, onBoundsChange]);
  
  return null;
}

function FitBoundsToWaypoints({ waypoints, savedPOIs }) {
  const map = useMap();
  const hasInitialized = useRef(false);
  
  useEffect(() => {
    if (hasInitialized.current) return;
    
    const allPoints = [];
    
    if (waypoints && waypoints.length > 0) {
      allPoints.push(...waypoints.map(wp => [wp.lat, wp.lon]));
    }
    
    if (savedPOIs && savedPOIs.length > 0) {
      allPoints.push(...savedPOIs.map(poi => [poi.lat, poi.lon]));
    }
    
    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [50, 50] });
      hasInitialized.current = true;
    }
  }, [waypoints, savedPOIs, map]);
  
  return null;
}

export default TourEditor;
